import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import guru from '../assets/logo/GuruLogo.png';
import vidya from '../assets/logo/VidyaLogo.png';
import suraksha from '../assets/logo/SurakshaLogo.png';
import mantrAi from '../assets/logo/MantraiLogo.png';
import vedaQuantum from '../assets/logo/Quantum.AI.png';
import MolecularScix from '../assets/logo/MolecularScix.jpeg';
import llmsandbox from '../assets/logo/SandboxLogo.png';
import './ProductSection.css';

const products = [
  {
    image: MolecularScix,
    title: "MolecularSciX.ai - AI for Molecular Discovery",
    category: "Scientific AI",
    outcome: "Accelerate molecular design and R&D decisions.",
    link: "/molecular",
    description: "MolecularSciX.ai empowers pharma, biotech, and research teams with AI-powered tools for faster molecular design, prediction, optimization, and scientific decision-making."
  },
  {
    image: guru,
    title: "ChatBot (Guru) - ChatBot Solutions",
    category: "Customer AI",
    outcome: "Automate conversations across voice and text.",
    link: "/product#guru",
    description: "Advanced AI-powered chatbot solution offering seamless voice and text-based interactions. Perfect for customer service automation and engagement enhancement."
  },
  {
    image: vidya,
    title: "Vidya - Applied AI in Education System",
    category: "Education AI",
    outcome: "Personalize learning paths with cognitive insights.",
    link: "/education",
    description: "Revolutionary AI-driven educational platform delivering personalized learning experiences through cognitive solutions and guided communication pathways."
  },
  {
    image: suraksha,
    title: "Suraksha - Security Framework By Vedalytics",
    category: "Security AI",
    outcome: "Strengthen access, identity, and protection.",
    link: "/product#suraksha",
    description: "Comprehensive security framework offering robust authentication and authorization solutions with flexible integration capabilities for enterprise systems."
  },
  {
    image: mantrAi,
    title: "MantrAI - AI-powered coding assistant",
    category: "Developer AI",
    outcome: "Speed up code generation and engineering flow.",
    link: "/product#mantrai",
    description: "MantrAI is an AI-powered developer tool designed to accelerate coding and streamline the development process. It assists developers with intelligent code generation."
  },
  {
    image: vedaQuantum,
    title: "Quantum.AI - Secure Local Generative AI",
    category: "Private GenAI",
    outcome: "Use enterprise data without losing control.",
    link: "/product#quantum-ai",
    description: "Quantum.AI is a secure local AI solution that harnesses your data to streamline operations and boost engagement with advanced Generative AI."
  },
  {
    image: llmsandbox,
    title: "LLM Sandbox - AI-powered coding assistant",
    category: "LLM Platform",
    outcome: "Prototype and test custom LLM workflows.",
    link: "/product#llm-sandbox",
    description: "The LLM Sandbox is a specialized platform designed to boost the productivity of engineering teams by offering a customized environment for creating and testing LLM-based solutions."
  }
];

const ProductCard = ({ frontImage, title, description, category, outcome, link }) => {
  return (
    <article className="flip-card product-tile h-full" tabIndex="0">
      <div className="flip-card-inner">
        <div className="flip-card-front product-tile-face">
          <div className="product-tile-topline">
            <span>{category}</span>
          </div>

          <div className="product-tile-body">
            <div className="product-logo-frame">
              <img src={frontImage} loading="lazy" alt={title} />
            </div>
            <h6 className="product-tile-title">{title}</h6>
            <p className="product-tile-outcome">{outcome}</p>
          </div>
        </div>

        <div className="flip-card-back product-tile-face product-tile-back">
          <div>
            <span className="product-tile-chip">{category}</span>
            <h6 className="product-tile-back-title">{title}</h6>
            <p className="product-tile-description">{description}</p>
          </div>
          <Link className="product-tile-link" to={link}>
            Explore product
          </Link>
        </div>
      </div>
    </article>
  );
};

const ProductSection = () => {
  const [startIndex, setStartIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const carouselRef = useRef(null);
  const autoplayIntervalRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);

  const visibleProducts = () => {
    const result = [];
    for (let i = 0; i < 4; i++) {
      const index = (startIndex + i) % products.length;
      result.push({
        ...products[index],
        position: i
      });
    }
    return result;
  };

  const handleNext = useCallback(() => {
    if (isTransitioning) return;

    setIsTransitioning(true);

    setTimeout(() => {
      setStartIndex((prevIndex) => (prevIndex + 1) % products.length);
      setIsTransitioning(false);
    }, 300);
  }, [isTransitioning]);

  const handlePrev = useCallback(() => {
    if (isTransitioning) return;

    setIsTransitioning(true);

    setTimeout(() => {
      setStartIndex((prevIndex) => (prevIndex - 1 + products.length) % products.length);
      setIsTransitioning(false);
    }, 300);
  }, [isTransitioning]);

  useEffect(() => {
    clearInterval(autoplayIntervalRef.current);

    if (!isPaused && !isTransitioning) {
      autoplayIntervalRef.current = setInterval(() => {
        handleNext();
      }, 3000);
    }

    return () => {
      clearInterval(autoplayIntervalRef.current);
    };
  }, [handleNext, isPaused, isTransitioning]);

  const handleIndicatorClick = (index) => {
    if (isTransitioning) return;

    setIsTransitioning(true);

    setTimeout(() => {
      setStartIndex(index);
      setIsTransitioning(false);
    }, 300);
  };

  return (
    <section className="product-showcase container-fluid">
      <div className="heading mx-2">
        <h5 className="text-primary mt-4"><b>PRODUCTS</b></h5>
        <p style={{ color: 'var(--content_color)' }} className="ser-content mx-auto my-3 hcs hf ">
          Next-gen solutions to drive business growth
        </p>
      </div>

      <div
        className="product-carousel-container position-relative py-3"
        ref={carouselRef}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div className="row px-4">
          {visibleProducts().map((product, index) => (
            <div
              className="col-12 col-sm-6 col-md-3 p-3"
              key={`card-${startIndex}-${index}`}
            >
              <ProductCard
                frontImage={product.image}
                title={product.title}
                description={product.description}
                category={product.category}
                outcome={product.outcome}
                link={product.link}
              />
            </div>
          ))}
        </div>

        <button
          className="carousel-control-prev"
          type="button"
          onClick={handlePrev}
          disabled={isTransitioning}
          aria-label="Previous"
        >
          <span className="carousel-control-prev-icon" aria-hidden="true"></span>
        </button>

        <button
          className="carousel-control-next"
          type="button"
          onClick={handleNext}
          disabled={isTransitioning}
          aria-label="Next"
        >
          <span className="carousel-control-next-icon" aria-hidden="true"></span>
        </button>

        <div className="carousel-indicators">
          {products.map((_, index) => (
            <button
              key={index}
              type="button"
              className={startIndex === index ? "active" : ""}
              onClick={() => handleIndicatorClick(index)}
              aria-label={`Slide ${index + 1}`}
              disabled={isTransitioning}
            ></button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProductSection;
